/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2023 Open Assessment Technologies SA ;
 */

import { __ } from '@oat-sa-private/ui-core';
import { exponentLeft, exponentRight, subscriptRight, symbols } from '@oat-sa/tao-calculator';

/**
 * @type {calculatorLabel[]} - A list of label definitions keyed by identifier
 */
export const labels = {
    // Digits definition
    NUM0: {
        type: 'text',
        value: '0'
    },
    NUM1: {
        type: 'text',
        value: '1'
    },
    NUM2: {
        type: 'text',
        value: '2'
    },
    NUM3: {
        type: 'text',
        value: '3'
    },
    NUM4: {
        type: 'text',
        value: '4'
    },
    NUM5: {
        type: 'text',
        value: '5'
    },
    NUM6: {
        type: 'text',
        value: '6'
    },
    NUM7: {
        type: 'text',
        value: '7'
    },
    NUM8: {
        type: 'text',
        value: '8'
    },
    NUM9: {
        type: 'text',
        value: '9'
    },
    DOT: {
        type: 'text',
        value: '.',
        ariaValue: __('decimal')
    },
    EXP10: {
        type: 'text',
        value: `${symbols.multiply}10`,
        ariaValue: __('multiply by ten')
    },
    POW10: {
        type: 'text',
        value: exponentRight('10', 'x'),
        ariaValue: __('10 raised to the nth')
    },

    // Aggregators
    LPAR: {
        type: 'text',
        value: '(',
        ariaValue: __('left parenthesis')
    },
    RPAR: {
        type: 'text',
        value: ')',
        ariaValue: __('right parenthesis')
    },

    // Operators
    SUB: {
        type: 'text',
        value: symbols.minus,
        ariaValue: __('subtraction')
    },
    ADD: {
        type: 'text',
        value: symbols.plus,
        ariaValue: __('addition')
    },
    POS: {
        type: 'text',
        value: symbols.positive,
        ariaValue: __('positive')
    },
    NEG: {
        type: 'text',
        value: symbols.negative,
        ariaValue: __('negative')
    },
    MUL: {
        type: 'text',
        value: symbols.multiply,
        ariaValue: __('multiplication')
    },
    DIV: {
        type: 'text',
        value: symbols.divide,
        ariaValue: __('division')
    },
    MOD: {
        type: 'text',
        value: __('modulo')
    },
    POW: {
        type: 'text',
        value: exponentRight('x', 'y'),
        ariaValue: __('X raised to the Y power')
    },
    POW2: {
        type: 'text',
        value: exponentRight('x', '2'),
        ariaValue: __('squared')
    },
    POW3: {
        type: 'text',
        value: exponentRight('x', '3'),
        ariaValue: __('cubed')
    },
    POWMINUSONE: {
        type: 'text',
        value: exponentRight('x', symbols.minusOne),
        ariaValue: __('reciprocal')
    },
    FAC: {
        type: 'text',
        value: '!',
        ariaValue: __('factorial')
    },
    PERCENT: {
        type: 'text',
        value: '%',
        ariaValue: __('percent')
    },
    ASSIGN: {
        type: 'text',
        value: '=',
        ariaValue: __('assign')
    },

    // Variables
    ANS: {
        type: 'text',
        value: __('ANS'),
        ariaValue: __('last answer')
    },

    // Constants
    PI: {
        type: 'text',
        value: symbols.pi,
        ariaValue: __('pi')
    },
    E: {
        type: 'text',
        value: symbols.euler,
        ariaValue: __("euler's number")
    },

    // Errors
    NAN: {
        type: 'text',
        value: __('Error')
    },
    INFINITY: {
        type: 'text',
        value: __('Infinity')
    },
    ERROR: {
        type: 'text',
        value: __('Syntax error')
    },

    // Functions
    EXP: {
        type: 'text',
        value: __('exp'),
        ariaValue: __('exponential')
    },
    EXPX: {
        type: 'text',
        value: exponentRight(symbols.euler, 'x'),
        ariaValue: __('natural exponent raised to the power of x')
    },
    SQRT: {
        type: 'text',
        value: symbols.squareRoot,
        ariaValue: __('square root')
    },
    CBRT: {
        type: 'text',
        value: exponentLeft(symbols.squareRoot, '3'),
        ariaValue: __('cube root')
    },
    NTHRT: {
        type: 'text',
        value: `${exponentLeft(symbols.squareRoot, 'y')}x`,
        ariaValue: __('nth root of unity')
    },
    SIN: {
        type: 'text',
        value: __('sin'),
        ariaValue: __('sine')
    },
    COS: {
        type: 'text',
        value: __('cos'),
        ariaValue: __('cosine')
    },
    TAN: {
        type: 'text',
        value: __('tan'),
        ariaValue: __('tangent')
    },
    ASIN: {
        type: 'text',
        value: exponentRight('sin', symbols.minusOne),
        ariaValue: __('arcsine')
    },
    ACOS: {
        type: 'text',
        value: exponentRight('cos', symbols.minusOne),
        ariaValue: __('arccosine')
    },
    ATAN: {
        type: 'text',
        value: exponentRight('tan', symbols.minusOne),
        ariaValue: __('arctangent')
    },
    SINH: {
        type: 'text',
        value: __('sinh'),
        ariaValue: __('hyperbolic sine')
    },
    COSH: {
        type: 'text',
        value: __('cosh'),
        ariaValue: __('hyperbolic cosine')
    },
    TANH: {
        type: 'text',
        value: __('tanh'),
        ariaValue: __('hyperbolic tangent')
    },
    ASINH: {
        type: 'text',
        value: exponentRight('sinh', symbols.minusOne),
        ariaValue: __('hyperbolic arcsine')
    },
    ACOSH: {
        type: 'text',
        value: exponentRight('cosh', symbols.minusOne),
        ariaValue: __('hyperbolic arccosine')
    },
    ATANH: {
        type: 'text',
        value: exponentRight('tanh', symbols.minusOne),
        ariaValue: __('hyperbolic arctangent')
    },
    LN: {
        type: 'text',
        value: __('ln'),
        ariaValue: __('natural logarithm')
    },
    LOG: {
        type: 'text',
        value: subscriptRight('log', '10'),
        ariaValue: __('logarithm base 10')
    },

    // Actions
    CLEAR: {
        type: 'text',
        value: __('AC'),
        ariaValue: __('all clear')
    },
    EXECUTE: {
        type: 'text',
        value: __('='),
        ariaValue: __('equals')
    },
    UP: {
        type: 'icon',
        value: 'chevron-top-16',
        ariaValue: __('recall previous expression')
    },
    DOWN: {
        type: 'icon',
        value: 'chevron-bottom-16',
        ariaValue: __('recall following expression')
    },
    DEL: {
        type: 'icon',
        value: 'backspace-24',
        ariaValue: __('remove last input')
    },
    DEGREE: {
        type: 'text',
        value: 'Deg',
        ariaValue: __('degree')
    },
    RADIAN: {
        type: 'text',
        value: 'Rad',
        ariaValue: __('radian')
    },
    SIGN: {
        type: 'text',
        value: '&plusmn;',
        ariaValue: __('plus or minus')
    }
};

/**
 * @type {termAriaLabel[]} - A list of aria label definitions for terms keyed by term identifier
 */
export const termAriaLabels = {
    DOT: __('point'),
    EXP10: __('multiply by ten in the power of'),
    LPAR: __('left parenthesis'),
    RPAR: __('right parenthesis'),
    COMMA: __('comma'),
    ELLIPSIS: __('ellipsis'),
    SUB: __('subtract'),
    NEG: __('minus'),
    ADD: __('add'),
    POS: __('plus'),
    MUL: __('multiply by'),
    DIV: __('divide by'),
    MOD: __('modulo'),
    POW: __('to the power of'),
    FAC: __('factorial'),
    ASSIGN: __('equals'),
    PERCENT: __('percent'),
    ANS: __('last answer'),
    MEM: __('memorized value'),
    PI: __('pi number'),
    E: __('euler number'),
    NAN: __('error'),
    INFINITY: __('infinity'),
    ERROR: __('syntax error'),
    EXP: __('exponential'),
    SQRT: __('square root of'),
    CBRT: __('cube root of'),
    NTHRT: __('nth root of'),
    FLOOR: __('floor value of'),
    CEIL: __('ceil value of'),
    ROUND: __('round value of'),
    TRUNC: __('truncate value of'),
    SIN: __('sine of'),
    COS: __('cosine of'),
    TAN: __('tangent of'),
    ASIN: __('arcsine of'),
    ACOS: __('arccosine of'),
    ATAN: __('arctangent of'),
    SINH: __('hyperbolic sine of'),
    COSH: __('hyperbolic cosine of'),
    TANH: __('hyperbolic tangent of'),
    ASINH: __('hyperbolic arcsine of'),
    ACOSH: __('hyperbolic arccosine of'),
    ATANH: __('hyperbolic arctangent of'),
    LN: __('natural logarithm of'),
    LOG: __('logarithm of'),
    LG: __('logarithm base ten of'),
    LOG10: __('logarithm base ten of'),
    ABS: __('absolute value of'),
    RAND: __('random value')
};

/**
 * Gets the label definition for a given identifier
 * @param {string} label - The identifier of the label
 * @returns {calculatorLabel} - Returns a label definition for the requested identifier
 */
export function getLabel(label) {
    if (labels[label]) {
        return labels[label];
    }

    return {
        type: 'text',
        value: label,
        ariaValue: label
    };
}

/**
 * Gets the label type for a given identifier
 * @param {string} label - The identifier of the label
 * @returns {string} - Returns the type of the given label
 */
export function getLabelType(label) {
    const definition = getLabel(label);
    return definition.type;
}

/**
 * Gets the label value for a given identifier
 * @param {string} label - The identifier of the label
 * @returns {string} - Returns the value of the given label
 */
export function getLabelValue(label) {
    const definition = getLabel(label);
    return definition.value;
}

/**
 * Gets the aria label value for a given identifier
 * @param {string} label - The identifier of the label
 * @returns {string} - Returns the value of the given label
 */
export function getAriaLabelValue(label) {
    const definition = getLabel(label);
    return definition.ariaValue;
}

/**
 * Populates members of term array with ariaLabel property
 * @param {term[]} terms array of terms
 * @returns {term[]} array of ariaLabelled terms
 */
export function addAriaLabelsToTerms(terms) {
    terms.forEach(term => {
        term.ariaLabel = termAriaLabels[term.token];
    });
    return terms;
}

/**
 * @typedef {object} calculatorLabel
 * @property {string} type - The type of label, it can be either `text` or `icon`
 * @property {string} value - The actual label, it can be either some HTML or the icon identifier
 */
